import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import TicketService from '../../../services/TicketService';
import { useSelector } from 'react-redux';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchTicket();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.replies]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const response = await TicketService.getTicket(id);
      setTicket(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError('Failed to load ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) return;

    try {
      setSubmitting(true);
      await TicketService.addReply(id, replyText);
      setReplyText('');
      await fetchTicket();
    } catch (err) {
      console.error('Error adding reply:', err);
      setError('Failed to send reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await TicketService.adminUpdateTicket(id, { status: newStatus });
      await fetchTicket();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      open: 'danger',
      in_progress: 'warning',
      resolved: 'success',
      closed: 'secondary'
    };
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      low: 'success',
      medium: 'info',
      high: 'warning',
      urgent: 'danger'
    };
    return <Badge bg={variants[priority] || 'secondary'} className="text-uppercase">{priority}</Badge>;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      technical: 'Technical Support',
      billing: 'Billing',
      feature_request: 'Feature Request',
      general: 'General Inquiry',
      account: 'Account'
    };
    return labels[category] || category;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!ticket && !loading) {
    return (
      <Alert variant="danger">
        <i className="flaticon-warning mr-2"></i>
        Ticket not found or you don't have access to view it.
        <div className="mt-3">
          <Link to="/tickets" className="btn btn-outline-primary btn-sm">
            Back to Tickets
          </Link>
        </div>
      </Alert>
    );
  }

  return (
    <div className="ticket-detail">
      {/* Header */}
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h4 className="mb-1">#{ticket.id} - {ticket.title}</h4>
              <div className="text-muted small">
                Created by {ticket.user_name || ticket.user_email} on {formatDate(ticket.created_at)}
              </div>
            </div>
            <div className="d-flex gap-2 mt-2 mt-md-0">
              {getStatusBadge(ticket.status)}
              {getPriorityBadge(ticket.priority)}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          
          <Row className="mb-3">
            <Col md={6}>
              <strong>Category:</strong> {getCategoryLabel(ticket.category)}
            </Col>
            <Col md={6}>
              <strong>Last Updated:</strong> {formatDate(ticket.updated_at)}
            </Col>
          </Row>

          {isAdmin && (
            <div className="mb-3 p-3 bg-light rounded">
              <strong>Admin Actions:</strong>
              <div className="mt-2 d-flex gap-2 flex-wrap">
                <Button 
                  size="sm" 
                  variant={ticket.status === 'open' ? 'primary' : 'outline-primary'}
                  onClick={() => handleStatusChange('open')}
                >
                  Mark Open
                </Button>
                <Button 
                  size="sm" 
                  variant={ticket.status === 'in_progress' ? 'warning' : 'outline-warning'}
                  onClick={() => handleStatusChange('in_progress')}
                >
                  Mark In Progress
                </Button>
                <Button 
                  size="sm" 
                  variant={ticket.status === 'resolved' ? 'success' : 'outline-success'}
                  onClick={() => handleStatusChange('resolved')}
                >
                  Mark Resolved
                </Button>
                <Button 
                  size="sm" 
                  variant={ticket.status === 'closed' ? 'secondary' : 'outline-secondary'}
                  onClick={() => handleStatusChange('closed')}
                >
                  Close Ticket
                </Button>
              </div>
            </div>
          )}

          <div className="ticket-description p-3 bg-light rounded mb-3">
            <h6 className="mb-2">Description:</h6>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
          </div>

          <Link to="/tickets" className="btn btn-outline-secondary btn-sm">
            <i className="flaticon-left-arrow mr-2"></i>
            Back to Tickets
          </Link>
        </Card.Body>
      </Card>

      {/* Replies */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <i className="flaticon-conversation mr-2"></i>
            Conversation ({ticket.replies?.length || 0})
          </h5>
        </Card.Header>
        <Card.Body>
          {ticket.replies?.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="flaticon-chat" style={{ fontSize: '32px' }}></i>
              <p className="mt-2">No replies yet. Be the first to respond!</p>
            </div>
          ) : (
            <div className="replies-list mb-4" style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {ticket.replies?.map((reply, index) => (
                <div 
                  key={reply.id} 
                  className={`reply mb-3 p-3 rounded ${reply.user_id === ticket.user_id ? 'bg-light' : 'bg-primary bg-opacity-10'}`}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <strong>{reply.user_name || reply.user_email}</strong>
                      {reply.user_role === 'admin' && (
                        <Badge bg="primary" className="ms-2">Admin</Badge>
                      )}
                      {reply.is_internal && isAdmin && (
                        <Badge bg="warning" text="dark" className="ms-2">Internal</Badge>
                      )}
                    </div>
                    <small className="text-muted">{formatDate(reply.created_at)}</small>
                  </div>
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{reply.message}</p>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Reply Form */}
          {ticket.status !== 'closed' && (
            <Form onSubmit={handleSubmitReply}>
              <Form.Group className="mb-3">
                <Form.Label>Add Reply</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your message here..."
                  required
                />
              </Form.Group>
              
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  {ticket.status === 'resolved' && 'This ticket is resolved. You can still add replies.'}
                </small>
                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={submitting || !replyText.trim()}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="flaticon-send mr-2"></i>
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}

          {ticket.status === 'closed' && (
            <Alert variant="info" className="mb-0">
              <i className="flaticon-information mr-2"></i>
              This ticket is closed. Please create a new ticket if you need further assistance.
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TicketDetail;
